import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChannels, fetchDirectMessages } from '../../store/slices/channelSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import PresignedAvatar from '../common/PresignedAvatar';
import { resolveUsersByIds } from '../../realm/offlineLookups';
import {
  findUserForDm,
  isWorkloopDm,
  mergePeopleForLookup,
  normalizeMemberIds,
  userDisplayName,
} from '../../utils/directMessageUtils';
import { workloopInboxLabel } from '../../utils/organizationBrand';
import { formatPresenceLabel, presenceDotClass } from '../../utils/presenceLabelUtils';

const ForwardMessageModal = ({ isOpen, onClose, onChannelSelected, messageToForward, groupToForward, excludeChannelId, heading, overlayClassName = 'z-50' }) => {
  const dispatch = useDispatch();
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const { users = [] } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('channels'); // 'channels' or 'directMessages'
  const [dmUserDetails, setDmUserDetails] = useState([]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchChannels());
      dispatch(fetchDirectMessages());
      dispatch(fetchUsers());
      setSearchQuery('');
      setSelectedTab('channels');
    }
  }, [isOpen, dispatch]);

  // Fetch user details for direct messages
  useEffect(() => {
    const fetchDMUserDetails = async () => {
      if (directMessages.length === 0) {
        setDmUserDetails([]);
        return;
      }

      try {
        const allMemberIds = [...new Set(
          directMessages.flatMap((dm) => normalizeMemberIds(dm.memberIds))
        )].filter((id) => id !== String(currentUser?.id || ''));

        if (allMemberIds.length === 0) {
          setDmUserDetails([]);
          return;
        }

        const uniqueMemberIds = [...new Set(allMemberIds)];
        const resolved = await resolveUsersByIds(uniqueMemberIds, users);
        setDmUserDetails(resolved);
      } catch (error) {
        console.error('Error fetching DM user details:', error);
      }
    };

    if (isOpen && directMessages.length > 0) {
      fetchDMUserDetails();
    }
  }, [directMessages, currentUser?.id, isOpen, users]);

  const handleChannelSelect = async (channel) => {
    if (onChannelSelected) {
      await onChannelSelected(channel);
    }
    onClose();
  };

  const isExcluded = (channel) =>
    excludeChannelId && channel?.id && String(channel.id) === String(excludeChannelId);

  // Filter channels based on search query
  const filteredChannels = channels.filter((channel) => {
    if (isExcluded(channel)) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const channelName = (channel.name || '').toLowerCase();
    const channelDescription = (channel.description || '').toLowerCase();
    return channelName.includes(query) || channelDescription.includes(query);
  });

  const peopleForDms = mergePeopleForLookup(dmUserDetails, users);

  // Filter direct messages based on search query
  const filteredDirectMessages = directMessages.filter((dm) => {
    if (isExcluded(dm)) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    if (isWorkloopDm(dm)) {
      const label = workloopInboxLabel(currentUser, dm).toLowerCase();
      return label.includes(query) || 'workloop'.includes(query);
    }
    const otherUser = findUserForDm(dm, currentUser?.id, peopleForDms);
    const fullName = (userDisplayName(otherUser) || dm.name || '').toLowerCase();
    const username = String(otherUser?.username || '').toLowerCase();
    return fullName.includes(query) || username.includes(query);
  });

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${overlayClassName}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {heading || (groupToForward ? 'Forward Group' : 'Forward Message')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {groupToForward && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-primary-500">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {groupToForward.groupName || 'Resource group'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {groupToForward.attachments?.length || 0} item{(groupToForward.attachments?.length || 0) === 1 ? '' : 's'}
                {groupToForward.senderName ? ` · from ${groupToForward.senderName}` : ''}
              </p>
            </div>
          )}

          {/* Message Preview */}
          {!groupToForward && messageToForward && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-primary-500">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  From: {messageToForward.senderName || 'Unknown User'}
                </span>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {messageToForward.content ? (
                  messageToForward.content.trim().startsWith('<') && messageToForward.content.includes('</') ? (
                    <div
                      className="ql-editor-content"
                      dangerouslySetInnerHTML={{ __html: messageToForward.content }}
                      style={{
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                        maxHeight: '3rem',
                        overflow: 'hidden',
                      }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap">{messageToForward.content}</p>
                  )
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">Message with attachments</p>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex space-x-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setSelectedTab('channels')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedTab === 'channels'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Channels
            </button>
            <button
              onClick={() => setSelectedTab('directMessages')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedTab === 'directMessages'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Direct Messages
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-4">
            <input
              type="text"
              placeholder={`Search ${selectedTab === 'channels' ? 'channels' : 'direct messages'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
          </div>
        </div>

        {/* Channels/DMs List */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {selectedTab === 'channels' ? (
            filteredChannels.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-4">
                {searchQuery ? 'No channels found' : 'No channels available'}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredChannels.map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel)}
                    className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <PresignedAvatar
                      fileKey={channel.imageUrl}
                      name={channel.name}
                      initials={channel.name?.substring(0, 2).toUpperCase() || 'CH'}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      fallbackClassName="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {channel.name}
                      </p>
                      {channel.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {channel.description}
                        </p>
                      )}
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )
          ) : (
            filteredDirectMessages.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-4">
                {searchQuery ? 'No direct messages found' : 'No direct messages available'}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredDirectMessages.map((dm) => {
                  const otherUser = findUserForDm(dm, currentUser?.id, peopleForDms);
                  const workloopChannel = isWorkloopDm(dm);
                  const displayName = workloopChannel
                    ? workloopInboxLabel(currentUser, dm)
                    : (userDisplayName(otherUser) || dm.name || 'Unknown User');

                  return (
                    <button
                      key={dm.id}
                      onClick={() => handleChannelSelect(dm)}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="relative flex-shrink-0">
                        <PresignedAvatar
                          fileKey={otherUser?.profilePicture}
                          name={displayName}
                          initials={otherUser?.firstName?.[0] || displayName?.[0] || 'U'}
                          className="w-10 h-10 rounded-full object-cover"
                          fallbackClassName="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        />
                        {!isWorkloopDm(dm) && (
                          <div
                            className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white dark:border-gray-800 rounded-full ${presenceDotClass(otherUser)}`}
                            title={formatPresenceLabel(otherUser)}
                          />
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {displayName}
                        </p>
                        {otherUser && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            @{otherUser.username}
                          </p>
                        )}
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ForwardMessageModal;
